import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;

/* ─── Gorgeous Overlay ─── */
const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50",
      "bg-black/50 backdrop-blur-[20px] saturate-150",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      "transition-opacity duration-300 ease-out",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

/* ─── Stunning Sheet Content ─── */
const SheetContent = React.forwardRef(({
  side = "right",
  size = "default",
  variant = "glass",
  className,
  children,
  showCloseButton = true,
  glowColor,
  ...props
}, ref) => {

  const sizeClasses = {
    sm: "sm:max-w-sm",
    default: "sm:max-w-md md:max-w-lg",
    lg: "sm:max-w-lg lg:max-w-xl",
    xl: "sm:max-w-xl lg:max-w-2xl",
    full: "sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl",
    viewport: "w-screen h-screen"
  };

  const variantClasses = {
    glass: cn(
      "bg-background/95 backdrop-blur-xl",
      "border-white/10",
      "shadow-[0_0_100px_rgba(0,0,0,0.3),_0_0_50px_rgba(0,0,0,0.2)]",
      "before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none",
      "before:bg-gradient-to-r before:from-primary/5 before:via-transparent before:to-primary/5",
      "before:opacity-50"
    ),
    solid: cn(
      "bg-background",
      "border-border/50",
      "shadow-2xl"
    ),
    gradient: cn(
      "bg-gradient-to-br from-background via-background to-muted/30",
      "border-primary/20",
      "shadow-[0_0_80px_rgba(var(--primary),0.15)]",
      "shadow-[0_0_30px_rgba(0,0,0,0.2)]"
    ),
    dark: cn(
      "bg-zinc-900/95 backdrop-blur-xl",
      "border-zinc-700/50",
      "shadow-[0_0_100px_rgba(0,0,0,0.5)]"
    )
  };

  const sideClasses = {
    right: cn(
      "inset-y-0 right-0 h-full",
      "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      "rounded-l-[2rem] sm:rounded-l-[2.5rem]",
      sizeClasses[size] || sizeClasses.default
    ),
    left: cn(
      "inset-y-0 left-0 h-full",
      "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
      "rounded-r-[2rem] sm:rounded-r-[2.5rem]",
      sizeClasses[size] || sizeClasses.default
    ),
    top: cn(
      "inset-x-0 top-0 w-full",
      "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
      "rounded-b-[2rem] sm:rounded-b-[2.5rem]",
      "max-h-[90vh]"
    ),
    bottom: cn(
      "inset-x-0 bottom-0 w-full",
      "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
      "rounded-t-[2rem] sm:rounded-t-[2.5rem]",
      "max-h-[90vh]"
    )
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          // Base
          "fixed z-50 flex flex-col",
          "transition-all ease-out",
          "data-[state=closed]:duration-300 data-[state=open]:duration-500",

          // Variant
          variantClasses[variant] || variantClasses.glass,

          // Side
          sideClasses[side],

          // Glow effect
          glowColor && `shadow-[0_0_60px_${glowColor}]`,

          className
        )}
        {...props}
      >
        {children}

        {showCloseButton && (
          <SheetPrimitive.Close
            className={cn(
              "absolute right-5 top-5 z-20",
              "flex h-9 w-9 items-center justify-center",
              "rounded-2xl",
              "bg-background/80 backdrop-blur-sm",
              "border border-border/50 shadow-sm",
              "text-muted-foreground/70 hover:text-foreground",
              "transition-all duration-200 ease-out",
              "hover:bg-muted/50 hover:scale-110 hover:shadow-md",
              "active:scale-95",
              "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

/* ─── Beautiful Header ─── */
const SheetHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col gap-2",
      "px-8 pt-8 pb-4",
      "border-b border-border/20",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

/* ─── Beautiful Footer ─── */
const SheetFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-3",
      "px-8 py-6 pt-4",
      "border-t border-border/20",
      "gap-3 sm:gap-2",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

/* ─── Stunning Title ─── */
const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      "text-xl md:text-2xl",
      "font-black tracking-tight text-foreground",
      "flex items-center gap-3",
      "leading-none",
      className
    )}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

/* ─── Beautiful Description ─── */
const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn(
      "text-sm text-muted-foreground",
      "leading-relaxed font-medium",
      className
    )}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
